import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { Button } from 'reactstrap'
import { FiFilePlus } from 'react-icons/fi'

import { SummaryRow } from './summary-row'
import { TitleRow } from './title-row'
import { InputRow } from './input-row'

import { CopyCostModal } from '../copy-cost-modal'
import { ConfirmSaveForm } from '../../../form'
import { FONTS } from '../../../../../constants'

const fallbackNull = (number) => (number === 'NaN' || number === Infinity || !number ? '0.00' : number)

export const TabPaneHardware = forwardRef((props, ref) => {
  const root = document.getElementById('root')
  const [showCopyModal, setShowCopyModal] = useState({ show: false })
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [hardwares, setHardwares] = useState([])
  const { isEdit, setIsEdit, rSelected, projectByID, editProjectInformation } = props

  const presale_snapshot = projectByID && projectByID.presale_snapshot ? projectByID.presale_snapshot.snapshot : null
  const isPresale = presale_snapshot && rSelected === 1
  const isImplementing = projectByID && projectByID.project_status === 'IMPLEMENTING'

  const totalCost = isPresale ? fallbackNull(presale_snapshot.$total_hardwares_cost) : fallbackNull(projectByID.$total_hardwares_cost)
  const totalPrice = isPresale ? fallbackNull(presale_snapshot.$total_hardwares_price) : fallbackNull(projectByID.$total_hardwares_price)
  const totalMargin = isPresale ? fallbackNull(presale_snapshot.$total_hardwares_margin) : fallbackNull(projectByID.$total_hardwares_margin)

  useEffect(() => {
    if (projectByID && projectByID.hardwares) {
      if (!isPresale) {
        setHardwares([...projectByID.hardwares])
      } else if (presale_snapshot.hardwares) {
        setHardwares([...presale_snapshot.hardwares])
      }
    }
  }, [projectByID, rSelected])

  useEffect(() => {
    if (isEdit) {
      root.scrollTop = root.scrollHeight - root.clientHeight
    }
  }, [hardwares.length])

  useImperativeHandle(ref, () => ({
    editHardware() {
      setShowConfirmSave(!showConfirmSave)
    },
  }))

  const handleClickAdd = () => {
    setHardwares(
      [
        ...hardwares,
        {
          type: 'HARDWARE',
          product_id: '',
          sub_project_code: '',
          description: '',
          delivery_date: '',
          credit_term: '15',
          cost: 0,
          price: 0,
        },
      ]
    )
  }

  const handleChangeDate = (e, id) => {
    const newHardwares = [...hardwares]
    const hardware = { ...newHardwares[id] }
    hardware.delivery_date = e || ''
    newHardwares[id] = hardware
    setHardwares(newHardwares)
  }

  const handleChangeInput = (e, id) => {
    const newHardwares = [...hardwares]
    const hardware = { ...newHardwares[id] }
    const field = e.target.name
    if (field === 'price' || field === 'cost') {
      hardware[field] = parseFloat(e.target.value.replace(/,/g, ''))
    } else {
      hardware[field] = e.target.value
    }
    newHardwares[id] = hardware
    setHardwares(newHardwares)
  }

  const handleChangeSelect = (e, id, name) => {
    const newHardwares = [...hardwares]
    const hardware = { ...newHardwares[id] }
    hardware[name] = e.value
    newHardwares[id] = hardware
    setHardwares(newHardwares)
  }

  const confirmEditHardware = () => {
    const payload = { ...projectByID }
    delete payload.project_type
    delete payload.created
    delete payload.project_approve_status
    delete payload.created_by
    delete payload.project_number
    delete payload.__v
    delete payload.project_status
    delete payload._id
    delete payload.updated_by
    payload.hardwares = [...hardwares]
    editProjectInformation(payload)
    setShowConfirmSave(false)
    setIsEdit(false)
  }

  const copyRow = (id) => {
    const newHardwares = [...hardwares, hardwares[id]]
    setHardwares(newHardwares)
  }

  const copyCost = () => {
    const numberOfCopies = document.getElementById('numberOfCopies')
    const id = showCopyModal.id
    const newHardwares = [...hardwares, ...Array(+numberOfCopies.value).fill(hardwares[id])]
    setHardwares(newHardwares)
    setShowCopyModal({ show: false })
  }

  const deleteRow = (id) => {
    const newHardwares = [...hardwares]
    newHardwares.splice(id, 1)
    setHardwares(newHardwares)
  }

  return (
    <Root>
      <ConfirmSaveForm
        open={ showConfirmSave }
        toggle={ () => setShowConfirmSave(!showConfirmSave) }
        confirmSave={ confirmEditHardware }
      />
      <CopyCostModal
        open={ showCopyModal.show }
        toggle={ setShowCopyModal }
        copyCost={ copyCost }
      />
      <SummaryRow
        totalCost={ totalCost }
        totalPrice={ totalPrice }
        totalMargin={ totalMargin }
      />
      <TitleRow />
      <RowWrapper>
        {hardwares.map((hardware, key) => (
          <InputRow
            id={ key.toString() }
            key={ key }
            hardware={ hardware }
            isEdit={ isEdit }
            isImplementing={ isImplementing }
            handleChangeInput={ handleChangeInput }
            handleChangeDate={ handleChangeDate }
            handleChangeSelect={ handleChangeSelect }
            setShowCopyModal={ setShowCopyModal }
            copyRow={ copyRow }
            deleteRow={ deleteRow }
          />
        ))}
      </RowWrapper>
      { isEdit && !isImplementing &&
        <AddButton onClick={ handleClickAdd }>
          <FiFilePlus style={ { fontSize: 16 } } />
          <AddText>Add a Cost</AddText>
        </AddButton>
      }
    </Root>
  )
})

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const RowWrapper = styled.div`
  height: 100%;
`

const AddButton = styled(Button)`
  width: 109px;
  height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #27AE60;
  border-radius: 4px;
  border: none;
  margin: 16px;
  padding: 0;

  svg {
    margin-right: 8px;
  }
`

const AddText = styled.span`
  font-family: ${FONTS.GOTHAM};
  font-size: ${FONTS.SMALL_SIZE};
  line-height: 180%;
`



// WEBPACK FOOTER //
// ./src/components/project-information/tab-content/tab-cost-planning/tab-pane-hardware/index.js